import React, { useEffect, useImperativeHandle, forwardRef, useRef } from "react";

const Scanner = forwardRef(({ onScan, paused = false, startChar = "@" }, ref) => {
  const inputRef = useRef(null);
  const isScanningRef = useRef(false);

  // ───────────────────────────── 유틸: 전각 → 반각 ─────────────────────────────
  const toHalfWidth = (s) => {
    return s.replace(/[\uFF01-\uFF5E]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
            .replace(/\u3000/g, " ")
            .replace(/[\uFF10-\uFF19]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFF10 + 0x30))
            .replace(/\uFF0D/g, "-") // 전각 하이픈
            .replace(/\uFF20/g, "@"); // 전각 @
  };

  // ─────────── 유틸: 한글 음절(가-힣) → 두벌식 키보드 영문으로 복원 ───────────
  const hangulToQwerty = (s) => {
    const L = ["r","R","s","e","E","f","a","q","Q","t","T","d","w","W","c","z","x","v","g"];
    const V = ["k","o","i","O","j","p","u","P","h","hk","ho","hl","y","n","nj","np","nl","b","m","ml","l"];
    const T = ["","r","R","rt","s","sw","sg","e","f","fr","fa","fq","ft","fx","fv","fg","a","q","qt","t","T","d","w","c","z","x","v","g"];

    let out = "";
    for (const ch of s) {
      const code = ch.codePointAt(0);

      // 완성형 음절
      if (code >= 0xAC00 && code <= 0xD7A3) {
        const sIndex = code - 0xAC00;
        const l = Math.floor(sIndex / 588);
        const v = Math.floor((sIndex % 588) / 28);
        const t = sIndex % 28;
        out += L[l] + V[v] + T[t];
        continue;
      }

      // 호환 자모(ㄱ-ㅎ, ㅏ-ㅣ)
      // 기본 자음
      const compatMap = {
        "ㄱ":"r","ㄲ":"R","ㄴ":"s","ㄷ":"e","ㄸ":"E","ㄹ":"f","ㅁ":"a","ㅂ":"q","ㅃ":"Q","ㅅ":"t","ㅆ":"T","ㅇ":"d",
        "ㅈ":"w","ㅉ":"W","ㅊ":"c","ㅋ":"z","ㅌ":"x","ㅍ":"v","ㅎ":"g",
        // 모음
        "ㅏ":"k","ㅐ":"o","ㅑ":"i","ㅒ":"O","ㅓ":"j","ㅔ":"p","ㅕ":"u","ㅖ":"P","ㅗ":"h","ㅛ":"y","ㅜ":"n","ㅠ":"b","ㅡ":"m","ㅣ":"l",
      };
      if (compatMap[ch]) {
        out += compatMap[ch];
        continue;
      }

      out += ch; // 한글 아닌 문자는 그대로
    }
    return out;
  };

  // ───────────────────────────── 후처리 파이프라인 ─────────────────────────────
  const finalize = (raw) => {
    let s = raw || "";

    // 1) 전각 → 반각
    s = toHalfWidth(s);

    // 2) 선두의 @ 한 개 제거
    s = s.replace(/^[\u0040\uFF20]/, "");

    // 3) 끝 개행/공백 제거
    s = s.replace(/[\r\n]+$/g, "").trim();

    // 4) 하이픈 압축
    s = s.replace(/-{2,}/g, "-");

    // 5) 한글 발견 시 두벌식 → 영문 복원
    if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(s)) {
      s = hangulToQwerty(s);
    }

    // 6) 최종 대문자 변환
    s = s.toUpperCase();

    return s;
  };

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus?.(),
    blur: () => inputRef.current?.blur?.(),
    getScanning: () => isScanningRef.current,
    setScanning: (v) => {
      isScanningRef.current = !!v;
      if (!v && inputRef.current) inputRef.current.value = "";
      if (v) inputRef.current?.focus?.();
    },
    start: () => {
      isScanningRef.current = true;
      if (inputRef.current) inputRef.current.value = "";
      inputRef.current?.focus?.();
    },
    cancel: () => {
      isScanningRef.current = false;
      if (inputRef.current) inputRef.current.value = "";
    },
  }));

  // 전역에서 시작문자(@)만 가로채서 포커스 이동
  useEffect(() => {
    if (paused) return;
    const onStarter = (e) => {
      if (isScanningRef.current) return;
      const startByKey = e.key === startChar;
      const startByCode = e.code === "Digit2" && e.shiftKey; // Shift+2 = '@'
      if (startByKey || startByCode) {
        e.preventDefault();
        e.stopPropagation();
        isScanningRef.current = true;
        if (inputRef.current) inputRef.current.value = "";
        inputRef.current?.focus?.();
      }
    };
    window.addEventListener("keydown", onStarter, true);
    return () => window.removeEventListener("keydown", onStarter, true);
  }, [paused, startChar]);

  const onKeyDown = (e) => {
    if (paused) return;
    if (!isScanningRef.current) return;

    // 포커스 유지
    if (e.key === "Tab") {
      e.preventDefault();
      inputRef.current?.focus?.();
      return;
    }

    // 확정: Enter
    if (e.key === "Enter") {
      e.preventDefault();
      const raw = inputRef.current?.value ?? "";
      const code = finalize(raw);
      isScanningRef.current = false;
      if (inputRef.current) inputRef.current.value = "";
      if (code) onScan(code);
      return;
    }

    // 취소: Esc
    if (e.key === "Escape") {
      e.preventDefault();
      isScanningRef.current = false;
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // 나머지는 **막지 않음**: 입력창이 raw를 그대로 누적하게 둠
    // (IME/중복 이벤트 포함 모든 키를 그대로 적재)
  };

  return (
    <input
      ref={inputRef}
      type="text"
      onKeyDown={onKeyDown}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      inputMode="none"
      style={{ opacity: 0, position: "absolute", pointerEvents: "none" }}
      aria-hidden="true"
    />
  );
});

export default Scanner;
